import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress';
import { 
  TrendingUp, 
  TrendingDown,
  Minus,
  Dumbbell,
  Scale,
  Brain,
  Calendar,
  Trophy,
  Loader2,
  ChevronDown,
  ChevronUp,
  Flame,
  Target
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProgressionDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/me/dashboard-progresion`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching progression dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-zinc-900 to-zinc-800 border-zinc-700">
        <CardContent className="p-4 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'adelantado': return 'text-green-400';
      case 'en_tiempo': return 'text-primary';
      case 'atrasado': return 'text-red-400';
      default: return 'text-muted-foreground';
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'adelantado': return <TrendingUp className="h-4 w-4" />;
      case 'atrasado': return <TrendingDown className="h-4 w-4" />;
      default: return <Minus className="h-4 w-4" />;
    }
  };

  const getTendenciaColor = (tendencia) => {
    switch (tendencia) {
      case 'mejora': return 'text-green-400';
      case 'empeora': return 'text-red-400';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <Card className="bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 border-zinc-700 overflow-hidden">
      <CardContent className="p-0">
        {/* Header con puntuación global */}
        <div 
          className="p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center">
                  <Trophy className="h-7 w-7 text-primary" />
                </div>
                <div className="absolute -bottom-1 -right-1 bg-zinc-800 rounded-full px-2 py-0.5 text-xs font-bold text-primary border border-primary/30">
                  {data.puntuacion_global}
                </div>
              </div>
              <div>
                <h3 className="font-bold text-foreground">Tu Progreso</h3>
                <p className="text-xs text-muted-foreground">
                  {data.sesiones_totales} sesiones completadas
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Mini indicadores */}
              <div className="flex gap-1">
                {data.fuerza.mejora_porcentaje > 0 && (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs font-medium flex items-center gap-1">
                    <Dumbbell className="h-3 w-3" />
                    +{data.fuerza.mejora_porcentaje}%
                  </span>
                )}
                {data.mesociclo.estado !== 'atrasado' && (
                  <span className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${
                    data.mesociclo.estado === 'adelantado' ? 'bg-green-500/20 text-green-400' : 'bg-primary/20 text-primary'
                  }`}>
                    <Calendar className="h-3 w-3" />
                    S{data.mesociclo.semana_actual}
                  </span>
                )}
              </div>
              {expanded ? <ChevronUp className="h-5 w-5 text-muted-foreground" /> : <ChevronDown className="h-5 w-5 text-muted-foreground" />}
            </div>
          </div>
        </div>

        {/* Contenido expandido */}
        {expanded && (
          <div className="px-4 pb-4 space-y-4 border-t border-zinc-700 pt-4 animate-in slide-in-from-top-2">
            
            {/* 💪 FUERZA */}
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-orange-500/20 rounded">
                    <Flame className="h-4 w-4 text-orange-400" />
                  </div>
                  <span className="font-semibold text-sm text-foreground">Fuerza</span>
                </div>
                <span className={`text-sm font-bold ${data.fuerza.mejora_porcentaje > 0 ? 'text-green-400' : 'text-muted-foreground'}`}>
                  {data.fuerza.mejora_porcentaje > 0 ? `+${data.fuerza.mejora_porcentaje}%` : 'Sin cambios'}
                </span>
              </div>
              
              {data.fuerza.mejora_porcentaje > 0 ? (
                <>
                  <p className="text-xs text-green-400 mb-2">
                    ¡Eres un {data.fuerza.mejora_porcentaje}% más fuerte que al inicio!
                  </p>
                  <div className="text-xs text-muted-foreground">
                    {data.fuerza.ejercicios_mejorados} de {data.fuerza.ejercicios_total} ejercicios mejoraron
                  </div>
                  {data.fuerza.detalle.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {data.fuerza.detalle.slice(0, 3).map((ej, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="text-foreground/80 truncate max-w-[150px]">{ej.ejercicio}</span>
                          <span className="text-green-400 font-medium">{ej.inicial}→{ej.actual}kg</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Completa más sesiones para ver tu progresión de fuerza
                </p>
              )}
            </div>

            {/* ⚖️ COMPOSICIÓN CORPORAL */}
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-500/20 rounded">
                    <Scale className="h-4 w-4 text-blue-400" />
                  </div>
                  <span className="font-semibold text-sm text-foreground">Composición</span>
                </div>
                <span className={`text-sm font-medium ${getTendenciaColor(data.composicion.tendencia)}`}>
                  {data.composicion.mejora_texto}
                </span>
              </div>
              
              {data.composicion.peso_actual ? (
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {data.composicion.peso_actual && (
                    <div className="text-center">
                      <p className="text-lg font-bold text-foreground">{data.composicion.peso_actual}</p>
                      <p className="text-xs text-muted-foreground">kg</p>
                    </div>
                  )}
                  {data.composicion.grasa_actual && (
                    <div className="text-center">
                      <p className="text-lg font-bold text-foreground">{data.composicion.grasa_actual}%</p>
                      <p className="text-xs text-muted-foreground">grasa</p>
                    </div>
                  )}
                  {data.composicion.musculo_actual && (
                    <div className="text-center">
                      <p className="text-lg font-bold text-foreground">{data.composicion.musculo_actual}%</p>
                      <p className="text-xs text-muted-foreground">músculo</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Registra tus datos corporales para ver tu progreso
                </p>
              )}
            </div>

            {/* 🧠 CONOCIMIENTO */}
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-500/20 rounded">
                    <Brain className="h-4 w-4 text-purple-400" />
                  </div>
                  <span className="font-semibold text-sm text-foreground">Conocimiento</span>
                </div>
                <span className="text-sm font-medium text-purple-400">
                  {data.conocimiento.pildoras_completadas}/{data.conocimiento.pildoras_total}
                </span>
              </div>
              <Progress value={data.conocimiento.porcentaje} className="h-2 bg-zinc-700" />
              <p className="text-xs text-muted-foreground mt-1">
                {data.conocimiento.porcentaje}% del contenido formativo completado
              </p>
            </div>

            {/* 📅 MESOCICLO */}
            <div className="bg-zinc-800/50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-primary/20 rounded">
                    <Target className="h-4 w-4 text-primary" />
                  </div>
                  <span className="font-semibold text-sm text-foreground">Mesociclo</span>
                </div>
                <div className={`flex items-center gap-1 text-sm font-medium ${getEstadoColor(data.mesociclo.estado)}`}>
                  {getEstadoIcon(data.mesociclo.estado)}
                  <span>Semana {data.mesociclo.semana_actual}/5</span>
                </div>
              </div>
              
              {/* Barra de progreso del mesociclo */}
              <div className="relative h-3 bg-zinc-700 rounded-full overflow-hidden mt-2">
                <div 
                  className={`absolute h-full transition-all duration-500 rounded-full ${
                    data.mesociclo.estado === 'adelantado' ? 'bg-green-500' :
                    data.mesociclo.estado === 'atrasado' ? 'bg-red-500' : 'bg-primary'
                  }`}
                  style={{ width: `${data.mesociclo.porcentaje_avance}%` }}
                />
                {/* Marcadores de semanas */}
                <div className="absolute inset-0 flex">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="flex-1 border-r border-zinc-600" />
                  ))}
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-2">
                <p className={`text-xs ${getEstadoColor(data.mesociclo.estado)}`}>
                  {data.mesociclo.mensaje}
                </p>
                <p className="text-xs text-muted-foreground">
                  {data.mesociclo.sesiones_completadas} sesiones
                </p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ProgressionDashboard;
